import { Fragment, useEffect, useRef, useState } from 'react';
import FetchInputProps from './FetchInput';
import { useAsync } from 'react-async';
import { SearchIcon, XIcon } from '@heroicons/react/outline';
import React from 'react';

/**
 * This component will provide the user with a normal text input element
 * However, this one can autocomplete based on the value of the input, whether
 * that be by an async function or data already provided. The important
 * parts here are the fetchFunction and fetchData properties.
 * 
 * fetchFunction: deferFn<T> - If fetchFunction is set, this will call an async function (deferFn<T>) usually used with useAsync.
 * fetchData: any[] - Alternatively, one could set the fetchData prop, which is an array or an object containing a data array
 * 
 * fetchProperty: string - This is the property by which the data will be filtered. Defaults to name.
 */
const FetchInput = React.memo(({
  name,
  label = '',
  sublabel = '',
  value = '',
  onBlur,
  onChange,
  onFocus,
  onClear,
  minLength = 0,
  maxLength = 50,
  type = 'text',
  /*placeholder = 'Begin typing...',*/
  placeholder = '',
  disabled = false,
  readonly = false,
  required = false,
  bgColor = 'white',
  borderColor = 'gray',
  textColor = 'gray',
  rounded = 'md',
  icon = undefined,
  shadow = 'md',
  containerClasses = '',
  delay = 500, //In milliseconds

  fetchProperty = 'name',
  altFetchProperty = 'label',
  valueProperty = 'name',
  fetchData = [],
  fetchFunction = undefined,
  returnRawValue = false,
}: FetchInputProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const [fieldValue, setFieldValue] = useState<string | number>(value);
  const [propValue, setPropValue] = useState<any>(null);
  const [isRunning, setIsRunning] = useState<boolean | null>(null);
  const [typedValue, setTypedValue] = useState<string>('');
  //const [previewPlaceholder, setPreviewPlaceholder] = useState<string>(placeholder ?? '');
  //const [isHovering, setIsHovering] = useState<boolean>(false);

  //CHANGE THIS TO FALSE TO ENABLE FORMER AUTOCOMPLETE FEATURE
  //DISABLED AS PART OF PTSU-234
  const [disableFetch, setDisableFetch] = useState<boolean>(true);

  /**
   * Resolves the async. This is also used for resolving the fetchData response.
   * Expects response to be an array or a data property which is an array
   * @param data any[]
   * @returns {void}
   */
  const resolve = (response: any): void => {
    //Get the response
    const data = response.data ?? response;

    if (!Array.isArray(data)) {
      throw new Error('FetchInput response is not an array');
    }

    //Get the matches
    const initialMatches = data.filter((x: any) => fetchProperty && x[fetchProperty] && x[fetchProperty].toLowerCase().includes(typeof (fieldValue) === 'string' ? fieldValue.toLowerCase() : fieldValue));
    const altMatches = data.filter((x: any) => altFetchProperty && x[altFetchProperty] && x[altFetchProperty].toLowerCase().includes(typeof (fieldValue) === 'string' ? fieldValue.toLowerCase() : fieldValue));
    const matches = [...initialMatches, ...altMatches];

    //If we have no matches, exit gracefully
    if (matches.length === 0) {
      setIsRunning(false);
      setFieldValue(fieldValue);
      setPropValue(fieldValue);
      onChange && onChange(fieldValue);
      return;
    }

    //Otherwise, filter down to one match
    const match = matches.length > 0 ? matches[0] : data[0];

    //Set the field value and the preview value
    //Since objects in javascript are basically arrays, we can treat them as such
    setFieldValue(match[fetchProperty]);
    setPropValue(match[valueProperty]);

    //Select the rest of the text that was autocompleted
    ref.current?.setSelectionRange(fieldValue.toString().length, match[fetchProperty].length);
    setIsRunning(false);

    //Finally, call the onChange handler, if one was provided
    onChange && onChange(returnRawValue ? fieldValue : (match[valueProperty] && match[valueProperty] !== '' ? match[valueProperty] : match[fetchProperty]));
  }

  /**
   * Async function for getting the data asynchronously
   * @returns void
   */
  const asyncData = useAsync({
    deferFn: fetchFunction,
    onResolve: resolve,
    onReject: (ex) => {
      console.error(`FetchInput reject on ${fetchFunction}: ${ex.message}`);
      setIsRunning(false);
    }
  });

  /**
   * Wrapper function so we can use asyncData.run
   * or the fetchData, if it was passed in
   * @returns {void}
   */
  const fetch = (): void => {
    if (fetchFunction) {
      asyncData.run(fieldValue);
    } else {
      return resolve(fetchData ? fetchData : []);
    }
  }

  /**
   * This effect runs whenever the input value changes.
   * It will wait before it sends the request, debouncing
   * the request if the value is changed within that time
   */
  useEffect(() => {
    //Verify our async operation is not currently running
    if (!disableFetch && !isRunning) {
      //Create our debounce timer
      const delayDebounceFn = setTimeout(async () => {
        if (typedValue !== '') {
          setIsRunning(true);
          fetch();
        }
      }, delay);

      return () => {
        setIsRunning(false);
        clearTimeout(delayDebounceFn);
      }
    }
  }, [typedValue])

  /**
   * When the input changes, we want to fire our fetch function
   */
  const handleChange = async (value: string) => {
    setFieldValue(value);
    setTypedValue(value);
    onChange && onChange(value);
  };

  /**
   * Clears the selection, which clears any value in the box
   */
  const clearSelection = () => {
    setFieldValue('');
    setTypedValue('');
    setPropValue('');
    onClear && onClear();
  }

  useEffect(() => {
    setFieldValue(value);
  }, [value]);

  return (
    <Fragment>
      {/* Primary Container */}
      <div className={`relative ${containerClasses} ${sublabel !== '' && sublabel !== null && sublabel !== undefined ? 'lg:relative lg:bottom-5' : ''}`}>
        {/* Labels */}
        <div className={`input-labels`}>
          {label !== '' && label !== null && label !== undefined ?
            <label htmlFor={name} className={`block primary  ${sublabel !== '' && sublabel !== null && sublabel !== undefined ? '' : 'mb-0.5 mt-0.5'}`}>
              {label} {(minLength > 0 || required) && <span className='pl-1 text-red-500'>*</span>}
            </label> :
            <></>}

          {/* Sublabel (if any) */}
          {sublabel !== '' && sublabel !== null && sublabel !== undefined ? <label htmlFor={name} className='block mb-1 text-sm font-semibold'>{sublabel}</label> : <></>}

          {/* Icon (if any) */}
          {icon && <div className='absolute text-2xl top-8 left-3'>
            {icon && icon === 'search' ? <SearchIcon fontSize={20} width={18} height={18} color='gray' /> : <></>}
            {icon && icon !== 'search' ? icon : <></>}
          </div>}

          {/* Clear Icon */}
          {/*isHovering &&*/}
          <div className='absolute text-2xl cursor-pointer top-8 right-3'>
            {fieldValue.toString().trim() === '' ? <></> : <XIcon fontSize={20} width={18} height={18} color='gray' onClick={clearSelection} /*style={{ display: isHovering ? 'inline-block' : 'none' }}*/ />}
          </div>
          {/*}*/}
        </div>

        {/* Input Element */}
        <input
          className={`
            bg-${bgColor}-200 
            appearance-none 
            border-1 
            border-${bgColor}-400 
            rounded-${rounded}
            w-full 
            py-2 
            px-${icon !== undefined ? '7 ' : '3'}
            pl-${icon ? '10 ' : '3'}
            pb-2
            text-${textColor}-700
            leading-tight 
            focus:outline-none 
            focus:bg-white 
            focus:border-${borderColor}-500
            shadow-${shadow}
          `}
          type={type}
          disabled={disabled}
          name={name}
          id={name}
          value={fieldValue}
          minLength={minLength}
          maxLength={maxLength}
          //onClick={onClick}
          onBlur={(e) => onBlur && onBlur(e)}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={(e) => onFocus && onFocus(e)}
          //placeholder={previewPlaceholder === '' ? placeholder : previewPlaceholder}
          placeholder={placeholder}
          autoComplete={'false'}
          data-lpignore={true}
          readOnly={readonly}
          ref={ref}
          required={required}
          style={{ paddingLeft: `${icon ? 2 : 0.7}rem` }}
        /*onMouseOver={() => setIsHovering(true)}
        onMouseOut={() => setIsHovering(false)}*/
        />

        {/* Required (if set) */}
        {/* {(minLength > 0 || required) && <p className='mt-2 italic text-red-500'>{label} is required.</p>} */}
      </div>
    </Fragment>
  );
});
export default FetchInput;